import React from 'react';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { ThemeProvider } from '@mui/material/styles';
import { setDefaultOptions } from 'date-fns';
import locale from 'date-fns/locale/pl';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { TssCacheProvider } from 'tss-react';

import axiosConfig from 'api/rest/axiosConfig';
import queryClient from 'api/rest/queryClient';
import { initAnalytics } from 'config/analyticsConfig';
import initFirebase from 'config/firebaseConfig';
import ErrorBoundary from 'containers/ErrorBoundary/ErrorBoundary';
import AppProviders from 'context/AppProviders';
import App from 'root/App/App';
import { captureTwaSession } from 'services/playBilling/playBilling';
import muiGlobalStyles from 'styles/MuiGlobalStyles';
import theme from 'styles/themes/theme';

import initSentry, { captureException } from './config/sentryConfig';

import 'simplebar/dist/simplebar.min.css';
import 'styles/reset.css';
import 'styles/print.css';
import './serviceWorker';

captureTwaSession();

initSentry();

axiosConfig();

initFirebase();

initAnalytics();

setDefaultOptions({ locale });

const muiCache = createCache({
  key: 'mui',
  prepend: true,
});

const tssCache = createCache({
  key: 'tss',
});

const root = createRoot(
  document.getElementById('root') as HTMLElement,
  {
    onRecoverableError: (error, errorInfo) => {
      captureException(error, {
        tags: { source: 'react_recoverable' },
        extra: { componentStack: errorInfo.componentStack },
        level: 'warning',
      });
    },
    onUncaughtError: (error: unknown, errorInfo: { componentStack?: string }) => {
      captureException(error, {
        tags: { source: 'react_uncaught' },
        extra: { componentStack: errorInfo?.componentStack },
      });
    },
  } as Parameters<typeof createRoot>[1],
);

root.render(
  <CacheProvider value={muiCache}>
    <TssCacheProvider value={tssCache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles styles={muiGlobalStyles} />
        <ErrorBoundary name='root'>
          <QueryClientProvider client={queryClient}>
            <AppProviders>
              <App />
            </AppProviders>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </TssCacheProvider>
  </CacheProvider>,
);
